import { and, eq, gt, lt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { secondsToHours, sumNetWorkSeconds } from '@/lib/dates'
import { useStore } from '@/lib/pocketbase'

interface JobSummary {
    totalHours: number
    entryCount: number
}

interface UseCustomerJobsSummaryOptions {
    jobIds: string[]
    startDate: string
    endDate: string
    enabled?: boolean
}

export function useCustomerJobsSummary({
    jobIds,
    startDate,
    endDate,
    enabled = true,
}: UseCustomerJobsSummaryOptions) {
    const [timeEntriesCollection] = useStore('time_entries')

    const { isLoading, data: rawEntries = [] } = useLiveQuery(
        q =>
            enabled && jobIds.length > 0
                ? q
                      .from({ entries: timeEntriesCollection })
                      .where(({ entries }) =>
                          and(
                              eq(entries.status, 'approved'),
                              gt(entries.clock_in, startDate),
                              lt(entries.clock_in, endDate)
                          )
                      )
                : undefined,
        [jobIds.join(','), startDate, endDate, enabled]
    )

    const summaryByJob = useMemo(() => {
        const summary: Record<string, JobSummary> = {}
        const jobIdSet = new Set(jobIds)
        const entriesByJob: Record<string, typeof rawEntries> = {}

        for (const jobId of jobIds) {
            summary[jobId] = { totalHours: 0, entryCount: 0 }
            entriesByJob[jobId] = []
        }

        for (const entry of rawEntries) {
            const jobId = entry.job
            if (jobIdSet.has(jobId) && entriesByJob[jobId]) {
                entriesByJob[jobId].push(entry)
                summary[jobId].entryCount += 1
            }
        }

        for (const jobId of jobIds) {
            summary[jobId].totalHours = secondsToHours(sumNetWorkSeconds(entriesByJob[jobId]))
        }

        return summary
    }, [rawEntries, jobIds])

    return {
        summaryByJob,
        isLoading,
    }
}
